import {
  createAutoTemplate,
  deleteAutoTemplate,
  updateAutoTemplate,
} from 'graphql/templates/mutations';
import { getDocumentTemplates, getTemplateTags } from 'graphql/templates/queries';
import { ListingEntry } from 'graphql/listings/queries';
import { useGetListings } from 'hooks/useGetListings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { AutoTemplate, NewTemplateType } from 'types/auto-templates';
import useAlert from 'utils/alert';
import { uniqueById } from 'utils/unique-by-id';
import { orderTemplatesAlphabetically } from './utils';

const EMPTY_ENTRIES: ListingEntry[] = [];

const useTemplates = () => {
  const showAlert = useAlert();
  const [open, setOpen] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [autoTemplates, setAutoTemplates] = useState<AutoTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AutoTemplate>();
  const [codeTemplates, setCodeTemplates] = useState<string[]>([]);

  const updateDataTemplates = useCallback((templates: AutoTemplate[]) => {
    setAutoTemplates(orderTemplatesAlphabetically(templates));
  }, []);

  const {
    isLoading, isFetching, isError, error,
  } = useQuery(['autoTemplates'], getDocumentTemplates, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      updateDataTemplates(data.getAutoTemplates);
    },
  });

  const { data: templateTagsResponse } = useQuery(['templateTags'], getTemplateTags, {
    refetchOnWindowFocus: false,
  });
  const partyRolesQuery = useGetListings({
    code: 'allowed_order_associations',
    queryConfig: { refetchOnWindowFocus: false },
  });
  const transactionsQuery = useGetListings({
    code: 'allowed_transactions',
    queryConfig: { refetchOnWindowFocus: false },
  });
  const typesQuery = useGetListings({
    code: 'allowed_types',
    queryConfig: { refetchOnWindowFocus: false },
  });
  const workflowsQuery = useGetListings({
    code: 'allowed_workflows',
    queryConfig: { refetchOnWindowFocus: false },
  });
  const partyRolesResponse = partyRolesQuery.data;
  const transactionsResponse = transactionsQuery.data;
  const typesResponse = typesQuery.data;
  const workflowsRespone = workflowsQuery.data;

  const listingQueries = [partyRolesQuery, transactionsQuery, typesQuery, workflowsQuery];
  const listingsLoading = listingQueries.some((q) => q.isLoading);
  const listingsError = listingQueries.some((q) => q.isError);
  const listingsReady = listingQueries.every((q) => q.isSuccess && !!q.data?.getListing?.entries);

  const partyRoles = partyRolesResponse?.getListing.entries ?? EMPTY_ENTRIES;

  const withAll = (entries?: { code: string; description: string }[]) => (entries
    ? [{ code: 'All', description: 'All' }, ...entries]
    : []);
  const transactionTypes = withAll(transactionsResponse?.getListing.entries);
  const types = withAll(typesResponse?.getListing.entries);
  const workflows = withAll(workflowsRespone?.getListing.entries);

  const { mutateAsync: createTemplateAsync, isLoading: isCreating } = useMutation(createAutoTemplate);
  const { mutateAsync: deleteTemplateAsync, isLoading: isDeleting } = useMutation(deleteAutoTemplate);
  const { mutateAsync: updateTemplateAsync, isLoading: isUpdating } = useMutation(updateAutoTemplate);

  const inFlight = useRef(false);
  const isMutating = isCreating || isDeleting || isUpdating;

  // Runs one mutation at a time; resolves true on success, false on rejection or when blocked.
  // Errors are surfaced by the global error handler, so they are swallowed here.
  const runExclusive = async (fn: () => Promise<void>) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    try {
      await fn();
      return true;
    } catch (e) {
      return false;
    } finally {
      inFlight.current = false;
    }
  };

  const tagsField = useMemo(() => {
    if (templateTagsResponse && templateTagsResponse.getTemplateTags) {
      return uniqueById(templateTagsResponse.getTemplateTags.filter((tag) => tag.kind === 'text'));
    }
    return [];
  }, [templateTagsResponse]);

  const tagsImages = useMemo(() => {
    if (templateTagsResponse && templateTagsResponse.getTemplateTags) {
      return uniqueById(templateTagsResponse.getTemplateTags.filter((tag) => tag.kind === 'image'));
    }
    return [];
  }, [templateTagsResponse]);

  const tagsSignatures = useMemo(() => {
    if (templateTagsResponse && templateTagsResponse.getTemplateTags) {
      return uniqueById(templateTagsResponse.getTemplateTags.filter((tag) => tag.kind === 'signatures'));
    }
    return [];
  }, [templateTagsResponse]);

  const handleDeleteTemplate = (id: string) => runExclusive(async () => {
    const data = await deleteTemplateAsync({ id });
    const deleted = data.deleteAutoTemplate;
    setSelectedTemplate((current) => (current?._id === deleted._id ? undefined : current));
    setAutoTemplates((prev) => orderTemplatesAlphabetically(prev.filter((t) => t._id !== deleted._id)));
    showAlert('Template was deleted', 'success');
  });

  const handleUpdateTemplate = (template: NewTemplateType) => runExclusive(async () => {
    const data = await updateTemplateAsync({
      id: template._id,
      ...template,
    });
    const updated = data.updateAutoTemplate;
    setAutoTemplates((prev) => orderTemplatesAlphabetically(
      prev.map((t) => (t._id === updated._id ? { ...updated } : t))
    ));
    setSelectedTemplate((current) => (current?._id === updated._id ? { ...updated } : current));
    showAlert('Template was updated', 'success');
  });

  useEffect(() => {
    setCodeTemplates(autoTemplates.map((template) => template.code));
  }, [autoTemplates]);

  const handleCreateTemplate = (newTemplate: NewTemplateType) => runExclusive(async () => {
    const data = await createTemplateAsync(newTemplate);
    setAutoTemplates((prev) => orderTemplatesAlphabetically([...prev, data.createAutoTemplate]));
    showAlert('Template was created', 'success');
  });

  return {
    autoTemplates,
    open,
    setOpen,
    handleCreateTemplate,
    handleDeleteTemplate,
    setSelectedTemplate,
    selectedTemplate,
    handleUpdateTemplate,
    setOpenEditModal,
    openEditModal,
    tagsField,
    tagsImages,
    tagsSignatures,
    codeTemplates,
    partyRoles,
    transactionTypes,
    types,
    workflows,
    isLoading,
    isFetching,
    isError,
    error,
    listingsLoading,
    listingsError,
    listingsReady,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
  };
};

export default useTemplates;
